import { Service } from '@toeverything/infra';

import type { DocMutationService } from '../../doc/services/doc-mutation-service';
import type { FolderApiService, FolderRole } from '../../folder-api';
import type { OfflineMutationOutboxService } from '../../offline-mutation-outbox';
import type {
  ObjectPermissionPrincipalRef,
  ObjectPermissionTargetRef,
  PermissionGrantMutationPayload,
  PermissionMutationPayload,
  PermissionRevokeMutationPayload,
  PermissionUpdateMutationPayload,
} from '../../offline-mutation-outbox/types';
import type { AccessibleFolderCacheStore } from '../../organize/stores/accessible-folder-cache';
import type { WorkspaceService } from '../../workspace';

export type ObjectPermissionMutationAction = 'grant' | 'revoke' | 'update';
export type ObjectPermissionMutationMode = 'online-only' | 'queue-when-offline';

export interface ObjectPermissionMutationInput {
  action: ObjectPermissionMutationAction;
  mode?: ObjectPermissionMutationMode;
  principal: ObjectPermissionPrincipalRef;
  role?: string;
  target: ObjectPermissionTargetRef;
}

/**
 * Runs before a permission change is applied, including changes queued while
 * offline. Rejecting stops the change, e.g. when the user cancels a warning
 * that apps lose access to data they read.
 */
export type ObjectPermissionChangeGate = (
  input: ObjectPermissionMutationInput
) => Promise<void>;

export interface PendingObjectPermissionAssignments {
  pendingInvites: readonly { userId: string; role: string }[];
  pendingGroupInvites: readonly { groupId: string; role: string }[];
}

export class ObjectPermissionMutationService extends Service {
  private changeGate: ObjectPermissionChangeGate | null = null;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly folderApiService: FolderApiService,
    private readonly docMutationService: DocMutationService,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore
  ) {
    super();
    this.registerExecutors();
  }

  /**
   * Registers the gate the UI provides for warnings before a change. Returns
   * the function that removes it again.
   */
  registerChangeGate(gate: ObjectPermissionChangeGate): () => void {
    this.changeGate = gate;
    return () => {
      if (this.changeGate === gate) {
        this.changeGate = null;
      }
    };
  }

  async execute(input: ObjectPermissionMutationInput): Promise<void> {
    await this.changeGate?.(input);
    if (input.mode === 'queue-when-offline') {
      await this.enqueue(input);
      return;
    }
    await this.executeOnline(input);
  }

  /**
   * Applies the user and group grants collected by a create dialog.
   *
   * All grants are attempted so one rejected principal never prevents the
   * remaining assignments from being applied. Grant construction and
   * execution semantics stay identical for documents and folders.
   */
  async grantPendingAssignments(
    target: ObjectPermissionTargetRef,
    assignments: PendingObjectPermissionAssignments
  ): Promise<void> {
    const grants = [
      ...assignments.pendingInvites.map(invite => ({
        principal: { id: invite.userId, kind: 'user' } as const,
        role: invite.role,
      })),
      ...assignments.pendingGroupInvites.map(invite => ({
        principal: { id: invite.groupId, kind: 'group' } as const,
        role: invite.role,
      })),
    ];
    const results = await Promise.allSettled(
      grants.map(grant =>
        this.execute({
          action: 'grant',
          principal: grant.principal,
          role: grant.role,
          target,
        })
      )
    );

    const failures = results.flatMap(result =>
      result.status === 'rejected' ? [result.reason] : []
    );
    if (failures.length > 0) {
      throw new AggregateError(
        failures,
        `Failed to apply ${failures.length} object permission assignment(s)`
      );
    }
  }

  async enqueue(input: ObjectPermissionMutationInput) {
    const operation = permissionOperation(input.action);
    const payload = this.toPayload(input);
    return this.outbox.enqueue({
      idempotencyKey: permissionIdempotencyKey(input),
      operation,
      payload,
    });
  }

  async executeOnline(input: ObjectPermissionMutationInput): Promise<void> {
    await this.executePayload(input.action, this.toPayload(input));
  }

  private async executePayload(
    action: ObjectPermissionMutationAction,
    payload: PermissionMutationPayload
  ): Promise<void> {
    if (payload.target.kind === 'doc') {
      await this.executeDocPermission(action, payload);
      return;
    }
    await this.executeFolderPermission(action, payload);
  }

  private async executeDocPermission(
    action: ObjectPermissionMutationAction,
    payload: PermissionMutationPayload
  ): Promise<void> {
    const docId = payload.target.id;
    if (payload.principal.kind === 'user') {
      if (action === 'revoke') {
        await this.docMutationService.revokeUserRole({
          docId,
          userId: payload.principal.id,
        });
        return;
      }
      if (action === 'grant') {
        await this.docMutationService.grantUserRoles({
          docId,
          role: requirePermissionRole(payload),
          userIds: [payload.principal.id],
        });
        return;
      }
      await this.docMutationService.updateUserRole({
        docId,
        role: requirePermissionRole(payload),
        userId: payload.principal.id,
      });
      return;
    }

    if (payload.principal.kind === 'mate') {
      if (action === 'revoke') {
        await this.docMutationService.revokeMateRole({
          docId,
          mateId: payload.principal.id,
        });
        return;
      }
      await this.docMutationService.grantMateRole({
        docId,
        mateId: payload.principal.id,
        role: requirePermissionRole(payload),
      });
      return;
    }

    if (action === 'revoke') {
      await this.docMutationService.revokeGroupRole({
        docId,
        groupId: payload.principal.id,
      });
      return;
    }
    await this.docMutationService.grantGroupRole({
      docId,
      groupId: payload.principal.id,
      role: requirePermissionRole(payload),
    });
  }

  private async executeFolderPermission(
    action: ObjectPermissionMutationAction,
    payload: PermissionMutationPayload
  ): Promise<void> {
    const workspaceId = this.workspaceService.workspace.id;
    const folderId = payload.target.id;
    if (payload.principal.kind === 'user') {
      if (action === 'revoke') {
        await this.folderApiService.revokeFolderRole(
          workspaceId,
          folderId,
          payload.principal.id
        );
      } else {
        await this.folderApiService.grantFolderRole(
          workspaceId,
          folderId,
          payload.principal.id,
          requirePermissionRole(payload) as FolderRole
        );
      }
      await this.refreshFolderPermissionCaches();
      return;
    }

    if (payload.principal.kind === 'mate') {
      if (action === 'revoke') {
        await this.folderApiService.revokeFolderMateRole(
          workspaceId,
          folderId,
          payload.principal.id
        );
      } else {
        await this.folderApiService.grantFolderMateRole(
          workspaceId,
          folderId,
          payload.principal.id,
          requirePermissionRole(payload)
        );
      }
      await this.refreshFolderPermissionCaches();
      return;
    }

    if (action === 'revoke') {
      await this.folderApiService.revokeFolderGroupRole(
        workspaceId,
        folderId,
        payload.principal.id
      );
    } else {
      await this.folderApiService.grantFolderGroupRole(
        workspaceId,
        folderId,
        payload.principal.id,
        requirePermissionRole(payload)
      );
    }
    await this.refreshFolderPermissionCaches();
  }

  private async refreshFolderPermissionCaches(): Promise<void> {
    await Promise.allSettled([
      this.accessibleFolderCache.fetchAccessibleFolders(),
      this.accessibleFolderCache.refreshRootFolderDocIds(),
    ]);
  }

  private registerExecutors(): void {
    this.disposables.push(
      this.outbox.registerExecutor('permission.grant', async mutation => {
        await this.executePayload('grant', mutation.payload);
      }),
      this.outbox.registerExecutor('permission.update', async mutation => {
        await this.executePayload('update', mutation.payload);
      }),
      this.outbox.registerExecutor('permission.revoke', async mutation => {
        await this.executePayload('revoke', mutation.payload);
      })
    );
  }

  private toPayload(
    input: ObjectPermissionMutationInput
  ):
    | PermissionGrantMutationPayload
    | PermissionRevokeMutationPayload
    | PermissionUpdateMutationPayload {
    const metadata =
      input.target.kind === 'doc'
        ? { affectedDocIds: [input.target.id] }
        : { affectedFolderIds: [input.target.id] };
    return {
      ...metadata,
      principal: input.principal,
      role: input.role,
      target: input.target,
    } as
      | PermissionGrantMutationPayload
      | PermissionRevokeMutationPayload
      | PermissionUpdateMutationPayload;
  }
}

function permissionOperation(action: ObjectPermissionMutationAction) {
  switch (action) {
    case 'grant':
      return 'permission.grant';
    case 'revoke':
      return 'permission.revoke';
    case 'update':
      return 'permission.update';
  }
}

function permissionIdempotencyKey(
  input: ObjectPermissionMutationInput
): string {
  return [
    'permission',
    input.action,
    input.target.kind,
    input.target.id,
    input.principal.kind,
    input.principal.id,
    input.role ?? 'none',
  ].join(':');
}

function requirePermissionRole(payload: PermissionMutationPayload): string {
  if (!payload.role) {
    throw new Error('Permission role is required');
  }
  return payload.role;
}
